import { Item } from '../models/item-model';
import { submitItem } from '../actions/async.action';
import {
  BatchComponentItem,
  BatchProgress,
  BatchError,
  CreatedItem,
  ApiError,
  ParentItemData,
} from '../models/batch-component-model';

/**
 * Shared utilities for batch component creation
 */

/**
 * Generate a unique ID for a batch component row
 * 
 * This generates a TEMPORARY client-side ID used only for React's key prop
 * and tracking which table row the user is editing in the UI.
 * 
 * This is NOT the same as Item.Data.Id which is the permanent server-side
 * identifier generated by the API when the item is created in the database.
 * 
 * Simple timestamp + random number is sufficient since these IDs are never
 * persisted and only need to be unique within the current UI session.
 */
let componentIdCounter = 0;
export const generateComponentId = (): string => {
  return `component-${Date.now()}-${++componentIdCounter}`;
};

/**
 * Create a new empty batch component
 */
export const createEmptyComponent = (): BatchComponentItem => ({
  id: generateComponentId(),
  ItemFullName: '',
  CodeName: '',
  Pm: '',
  PmName: '',
  ItemType: '',
});

/**
 * Build component data from batch component item and parent data
 */
export const buildComponentData = (
  component: BatchComponentItem,
  parentId: string,
  parentData: ParentItemData
): Item.Data => {
  const componentData: Item.Data = {
    ...Item.Data.getDefaultData(),
    ItemFullName: component.ItemFullName,
    CodeName: component.CodeName,
    Pm: component.Pm,
    PmName: component.PmName,
    parentId: parentId,
    ItemType: component.ItemType,
    // Set Form attribute for components
    Form: [
      { question: '1', version: '1', answer: 'Amazon' },
      { question: '2', version: '2', answer: component.ItemType },
    ],
    // Inherit from parent item
    BusinessLineAttorney: parentData.BusinessLineAttorney || '',
    BusinessLineAttorneyName: parentData.BusinessLineAttorneyName || '',
    OtherContributors: parentData.OtherContributors || [],
    Description: parentData.Description || '',
    OnlineDocumentation: parentData.OnlineDocumentation || '',
    MilitaryUse: parentData.MilitaryUse || '',
    ResearchProject: parentData.ResearchProject || '',
    CET: parentData.CET || [],
    Confidential: parentData.Confidential || 'No',
    SaaSRestrictions: parentData.SaaSRestrictions || {
      isEnterpriseManagementSoftware: '',
      isDesignAndManufacturingSoftware: '',
      isCNCSoftware: '',
    },
  };

  // Remove QA property if it exists
  delete (componentData as any).QA;

  return componentData;
};

/**
 * Extract error message from API error
 */
export const extractErrorMessage = (err: unknown): string => {
  const apiError = err as ApiError;
  return (
    apiError.response?.data?.errors?.map((e) => e.message).join(', ') ||
    'Unknown error'
  );
};

/**
 * Create multiple components in batch
 */
export const createComponentsBatch = async (
  components: BatchComponentItem[],
  parentId: string,
  parentData: ParentItemData,
  onProgress?: (progress: BatchProgress) => void
): Promise<{
  createdItems: CreatedItem[];
  errors: BatchError[];
}> => {
  const total = components.length;
  const createdItems: CreatedItem[] = [];
  const errors: BatchError[] = [];

  for (let i = 0; i < components.length; i++) {
    const component = components[i];

    // Report progress
    if (onProgress) {
      onProgress({
        current: i,
        total,
        message: `Creating component ${i + 1} of ${total}: ${component.ItemFullName}...`,
      });
    }

    try {
      const componentData = buildComponentData(component, parentId, parentData);
      const response = await submitItem(componentData);
      createdItems.push({
        name: component.ItemFullName,
        id: response.data.Id,
      });
    } catch (err) {
      console.error(`Failed to create component ${component.ItemFullName}:`, err);
      const errorMessage = extractErrorMessage(err);
      errors.push({ componentName: component.ItemFullName, error: errorMessage });
    }
  }

  // Final progress update
  if (onProgress) {
    onProgress({
      current: total,
      total,
      message: 'Related Item creation complete!',
    });
  }

  return { createdItems, errors };
};

/**
 * Validate that all required fields are filled for a batch component
 */
export const isComponentValid = (component: BatchComponentItem): boolean => {
  return !!(
    component.ItemFullName &&
    component.CodeName &&
    component.Pm &&
    component.ItemType
  );
};

/**
 * Validate that all components in a batch are valid
 */
export const areAllComponentsValid = (components: BatchComponentItem[]): boolean => {
  return components.length > 0 && components.every(isComponentValid);
};